import type {ChatModelAdapter, ChatModelRunResult} from "@assistant-ui/react";
import Session from "supertokens-auth-react/recipe/session";
import {VITE_API_DOMAIN} from "@/config";
import type {components} from "./api-types";

// ============================================================================
// Widget types - reused from generated API types
// ============================================================================

export type PostWidget = components["schemas"]["PostWidget"];
export type ChartWidget = components["schemas"]["ChartWidget"];
export type ChartDataPoint = components["schemas"]["ChartDataPoint"];
export type PostPlatform = PostWidget["platform"];

// Extensible widget container - each key is a widget type
export interface SourceWidgets {
  post?: PostWidget[];
  chart?: ChartWidget[];
}

interface SSEMessage {
  type: "text" | "done" | "error" | "sources";
  content?: string;
  message?: string;
  widgets?: SourceWidgets;
}

/**
 * Parse SSE data line into JSON object
 */
function parseSSEData(line: string): SSEMessage | null {
  if (!line.startsWith("data: ")) {
    return null;
  }

  try {
    const jsonStr = line.slice(6); // Remove "data: " prefix
    return JSON.parse(jsonStr) as SSEMessage;
  } catch {
    console.error("Failed to parse SSE data:", line);
    return null;
  }
}

/**
 * ChatModelAdapter that connects to the backend /api/chat/stream endpoint
 * with SuperTokens authentication and SSE streaming.
 */
export const createChatModelAdapter = (): ChatModelAdapter => ({
  async *run({messages, abortSignal}): AsyncGenerator<ChatModelRunResult> {
    // Get auth headers from SuperTokens
    const accessToken = await Session.getAccessToken();
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    // Convert messages to backend format
    const backendMessages = messages.map((msg) => ({
      role: msg.role,
      content: msg.content
        .filter((c) => c.type === "text")
        .map((c) => (c as {type: "text"; text: string}).text)
        .join("\n"),
    }));

    // Make request to backend streaming endpoint
    const response = await fetch(`${VITE_API_DOMAIN}/api/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        messages: backendMessages,
        model: "gpt-4o",
      }),
      signal: abortSignal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication failed. Please log in again.");
      }
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    // Read the SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";
    let sourceWidgets: SourceWidgets = {};

    try {
      while (true) {
        const {done, value} = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete lines from buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) {
            continue;
          }

          const message = parseSSEData(trimmedLine);
          if (!message) {
            continue;
          }

          switch (message.type) {
            case "text":
              if (message.content) {
                fullText += message.content;
                yield {
                  content: [{type: "text", text: fullText}],
                };
              }
              break;

            case "sources":
              // Collect source widgets for metadata
              if (message.widgets) {
                sourceWidgets = message.widgets;
              }
              break;

            case "done":
              // Final yield with metadata containing widgets
              yield {
                content: [{type: "text", text: fullText}],
                metadata: {
                  custom: {
                    widgets: sourceWidgets,
                  },
                },
              };
              return;

            case "error":
              throw new Error(message.message || "Unknown streaming error");
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
});
